import React, { useState, useEffect } from 'react';
import './styles/common.css';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import PasswordResetPage from './PasswordResetPage';
import UserProfilePage from './UserProfilePage';
import HeaderBar from './components/HeaderBar';
import HousekeeperListPage from './components/HousekeeperListPage';
import HousekeeperEditPage1 from './components/HousekeeperEditPage1';
import HousekeeperEditPage2 from './components/HousekeeperEditPage2';
import HousekeeperDetailPage from './components/HousekeeperDetailPage';
import AssessmentReviewPage from './components/AssessmentReviewPage';
import ShareResumePage from './components/ShareResumePage';
import PracticalPage from './components/PracticalPage';
import AdminPage from './components/AdminPage';
import BackgroundCheck from './components/BackgroundCheck';
import BackgroundCheckResult from './components/BackgroundCheckResult';
import IdCardImagePage from './components/IdCardImagePage';
import SalaryFlowPage from './components/SalaryFlowPage';
import { useAuth } from './hooks/useAuth';
import { useHousekeepers } from './hooks/useHousekeepers';

function App() {
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (path === '/admin') return 'admin';
    return 'home';
  };
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [pageParams, setPageParams] = useState({});
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { currentUser, userRole, canAdd, handleLoginSuccess, handleLogout } = useAuth();
  const { housekeepers, setHousekeepers, filteredHousekeepers, searchQuery, setSearchQuery, stats, fetchHousekeepers } = useHousekeepers();

  useEffect(() => {
    if (currentUser) fetchHousekeepers();
  }, [currentUser, fetchHousekeepers]);

  useEffect(() => {
    const handleNavigate = (e) => setCurrentPage(e.detail);
    window.addEventListener('navigate', handleNavigate);
    const handlePopState = () => {
      const path = window.location.pathname;
      const newPage = path === '/admin' ? 'admin' : 'home';
      setCurrentPage(newPage);
      // 回到主页时重新拉取并排序
      if (newPage === 'home' && currentUser) {
        fetchHousekeepers();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('navigate', handleNavigate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentUser, fetchHousekeepers]);

  const navigateTo = (page, params = {}) => {
    setCurrentPage(page);
    setPageParams(params);
    const pathMap = { admin: '/admin' };
    if (pathMap[page]) {
      window.history.pushState({}, '', pathMap[page]);
    } else if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
    // 每次回到主页都重新拉取并排序，确保新增/编辑/状态变更后顺序正确
    if (page === 'home' && currentUser) {
      fetchHousekeepers();
    }
  };

  const showHeader = currentUser && ['home', 'edit1', 'edit2', 'detail', 'assessment-review', 'practical', 'admin'].includes(currentPage);

  return (
    <div className="container">
      {currentPage === 'login' && (
        <LoginPage onLoginSuccess={(info) => { handleLoginSuccess(info); navigateTo('home'); }} onNavigateToRegister={() => navigateTo('register')} />
      )}

      {currentPage === 'register' && (
        <RegisterPage onNavigateToLogin={() => navigateTo('login')} />
      )}

      {currentPage === 'password-reset' && (
        <PasswordResetPage navigateTo={navigateTo} />
      )}

      {currentPage === 'share-view' && (
        <ShareResumePage
          shareToken={pageParams.shareToken}
        />
      )}

      {showHeader && (
        <HeaderBar
          currentUser={currentUser}
          userRole={userRole}
          onLogout={() => { handleLogout(); navigateTo('login'); }}
          onProfile={() => setIsProfileOpen(true)}
        />
      )}

      {currentPage === 'home' && (
        <HousekeeperListPage
          currentUser={currentUser}
          userRole={userRole}
          canAdd={canAdd}
          navigateTo={navigateTo}
          onLogout={() => { handleLogout(); navigateTo('home'); }}
          housekeepers={housekeepers}
          setHousekeepers={setHousekeepers}
          filteredHousekeepers={filteredHousekeepers}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          stats={stats}
          fetchHousekeepers={fetchHousekeepers}
          showGlobalHeader={showHeader}
        />
      )}

      {currentPage === 'edit1' && (
        <HousekeeperEditPage1
          id={pageParams.id}
          mode={pageParams.mode}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'edit2' && (
        <HousekeeperEditPage2
          id={pageParams.id}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'detail' && (
        <HousekeeperDetailPage
          id={pageParams.id}
          userRole={userRole}
          navigateTo={navigateTo}
          initialShowShare={pageParams.showShare}
        />
      )}

      {currentPage === 'assessment-review' && (
        <AssessmentReviewPage
          id={pageParams.id}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'practical' && (
        <PracticalPage
          id={pageParams.id}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'admin' && (
        <AdminPage />
      )}

      {currentPage === 'background-check' && (
        <BackgroundCheck
          id={pageParams.id}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'background-check-result' && (
        <BackgroundCheckResult
          id={pageParams.id}
          checkId={pageParams.checkId}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'idcard-image' && (
        <IdCardImagePage
          id={pageParams.id}
          navigateTo={navigateTo}
        />
      )}

      {currentPage === 'salary-flow' && (
        <SalaryFlowPage
          id={pageParams.id}
          navigateTo={navigateTo}
        />
      )}

      {isProfileOpen && (
        <UserProfilePage onClose={() => setIsProfileOpen(false)} />
      )}
    </div>
  );
}

export default App;